import React, { useState, useEffect } from 'react';
import { getSiteDrilldown } from '../api';
import {
  ArrowLeft, Lightning, Leaf, Drop, Gauge, Users,
  ShieldCheck, CurrencyDollar, ChartLineUp, MapPin
} from '@phosphor-icons/react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis
} from 'recharts';

function MetricTile({ icon: Icon, label, value, unit, color, delay }) {
  return (
    <div className={`bg-surface border border-border rounded-lg p-4 hover:border-tertiary/40 transition-all duration-200 animate-fade-in ${delay}`}>
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center mb-2`}>
        <Icon size={16} weight="duotone" className="text-white" />
      </div>
      <p className="text-[10px] font-body font-semibold uppercase tracking-[0.15em] text-tertiary">{label}</p>
      <p className="font-heading text-xl font-bold text-text-primary mt-0.5">
        {value}<span className="text-sm font-normal text-text-secondary ml-1">{unit}</span>
      </p>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface border border-border rounded-lg p-3 shadow-lg text-xs font-body">
      <p className="font-medium text-text-primary mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-text-secondary">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: <span className="font-medium text-text-primary">{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}</span>
        </p>
      ))}
    </div>
  );
}

export default function SiteDrilldown({ industry, site, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data: d } = await getSiteDrilldown(industry, site);
        setData(d);
      } catch (err) {
        console.error('Failed to load site data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [industry, site]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="site-drilldown-loading">
        <div className="h-10 w-48 bg-surface rounded-lg" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-surface border border-border rounded-lg" />)}
        </div>
        <div className="h-64 bg-surface border border-border rounded-lg" />
      </div>
    );
  }

  if (!data || !data.metrics) return null;

  const { metrics, trend, latest, site_info } = data;

  // Radar chart data for EESG balance
  const radarData = [
    { subject: 'Energy Eff.', value: Math.min(100, 100 - (metrics.avg_energy_intensity || 0) * 50), fullMark: 100 },
    { subject: 'Carbon Perf.', value: Math.min(100, 100 - (metrics.avg_carbon_intensity || 0) * 50), fullMark: 100 },
    { subject: 'OEE', value: metrics.avg_oee || 0, fullMark: 100 },
    { subject: 'Safety', value: Math.min(100, 100 - (metrics.avg_trir || 0) * 50), fullMark: 100 },
    { subject: 'Assurance', value: metrics.avg_assurance || 0, fullMark: 100 },
    { subject: 'Cost Eff.', value: Math.min(100, 100 - (metrics.avg_cost_per_unit || 0) / 5), fullMark: 100 },
  ];

  const controlDays = [
    { label: 'Green', value: metrics.green_days, color: '#2D6A4F' },
    { label: 'Amber', value: metrics.amber_days, color: '#F4A261' },
    { label: 'Red', value: metrics.red_days, color: '#E07A5F' },
  ];

  return (
    <div className="space-y-6" data-testid="site-drilldown">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <button
          data-testid="drilldown-back-button"
          onClick={onBack}
          className="p-2 rounded-lg border border-border hover:bg-background transition-colors"
        >
          <ArrowLeft size={18} className="text-text-primary" />
        </button>
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-text-primary tracking-tight">
            {site}
          </h1>
          <div className="flex items-center gap-3 mt-1">
            <span className="font-body text-sm text-text-secondary">{industry}</span>
            {site_info && (
              <>
                <span className="text-border">|</span>
                <span className="flex items-center gap-1 text-xs font-body text-tertiary">
                  <MapPin size={12} weight="fill" /> {site_info.region}
                </span>
                <span className="text-border">|</span>
                <span className="text-xs font-body text-tertiary">{site_info.type}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Metrics grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <MetricTile icon={Lightning} label="Avg Energy" value={metrics.avg_energy_mwh} unit="MWh" color="bg-primary" delay="stagger-1" />
        <MetricTile icon={Leaf} label="Avg CO2" value={metrics.avg_tco2e} unit="tCO2e" color="bg-secondary" delay="stagger-2" />
        <MetricTile icon={Drop} label="Avg Water" value={metrics.avg_water_m3} unit="m3" color="bg-info" delay="stagger-3" />
        <MetricTile icon={Gauge} label="Avg OEE" value={metrics.avg_oee} unit="%" color="bg-tertiary" delay="stagger-4" />
        <MetricTile icon={CurrencyDollar} label="Avg Cost/Unit" value={`$${metrics.avg_cost_per_unit}`} unit="" color="bg-secondary" delay="stagger-1" />
        <MetricTile icon={Users} label="Avg TRIR" value={metrics.avg_trir} unit="" color="bg-alert-warning" delay="stagger-2" />
        <MetricTile icon={ShieldCheck} label="Assurance" value={metrics.avg_assurance} unit="%" color="bg-primary" delay="stagger-3" />
        {metrics.avg_scrap_pct !== undefined && (
          <MetricTile icon={ChartLineUp} label="Avg Scrap" value={metrics.avg_scrap_pct} unit="%" color="bg-alert-critical" delay="stagger-4" />
        )}
      </div>

      {/* Control status bar */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <h3 className="font-heading text-base font-semibold text-text-primary mb-3">Control Status Distribution ({metrics.total_days} days)</h3>
        <div className="flex rounded-lg overflow-hidden h-6">
          {controlDays.filter(c => c.value > 0).map(c => (
            <div
              key={c.label}
              style={{ width: `${(c.value / metrics.total_days) * 100}%`, backgroundColor: c.color }}
              className="flex items-center justify-center text-[10px] font-body font-bold text-white"
            >
              {c.value > 1 && `${c.value}d`}
            </div>
          ))}
        </div>
        <div className="flex gap-6 mt-2">
          {controlDays.map(c => (
            <span key={c.label} className="flex items-center gap-1.5 text-xs font-body text-text-secondary">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
              {c.label}: {c.value} days
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Energy & CO2 trend */}
        <div className="lg:col-span-2 bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-1">Energy & Carbon Trend</h3>
          <p className="text-xs font-body text-text-secondary mb-4">30-day daily performance</p>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#888864' }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: '#888864' }} />
                <Tooltip content={<ChartTooltip />} />
                <Line type="monotone" dataKey="energy" name="Energy (MWh)" stroke="#3D4B32" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="co2" name="CO2 (tCO2e)" stroke="#675D3A" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* EESG Radar */}
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-1">EESG Balance</h3>
          <p className="text-xs font-body text-text-secondary mb-2">Performance profile</p>
          <div className="h-52">
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#E5E5E0" />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#888864' }} />
                <PolarRadiusAxis tick={false} domain={[0, 100]} />
                <Radar name="Performance" dataKey="value" stroke="#3D4B32" fill="#3D4B32" fillOpacity={0.2} strokeWidth={2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* OEE & Cost daily */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <h3 className="font-heading text-base font-semibold text-text-primary mb-1">OEE & Cost Daily Performance</h3>
        <p className="text-xs font-body text-text-secondary mb-4">Operational efficiency tracking</p>
        <div className="h-48">
          <ResponsiveContainer>
            <BarChart data={trend.slice(-14)}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#888864' }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: '#888864' }} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="oee" name="OEE %" fill="#3D4B32" radius={[3, 3, 0, 0]} />
              <Bar dataKey="cost" name="Cost/Unit" fill="#888864" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Latest reading */}
      {latest && (
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-base font-semibold text-text-primary mb-3">Latest Reading</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Output', value: latest.output },
              { label: 'Energy', value: `${latest.energy_mwh} MWh` },
              { label: 'CO2', value: `${latest.tco2e} tCO2e` },
              { label: 'Water', value: `${latest.water_m3} m3` },
              { label: 'OEE', value: `${latest.oee_pct}%` },
              { label: 'Status', value: latest.control_status },
            ].map((item, i) => (
              <div key={i} className="p-2 bg-background rounded-lg">
                <p className="text-[10px] font-body text-tertiary uppercase tracking-wider">{item.label}</p>
                <p className="font-heading text-sm font-bold text-text-primary mt-0.5">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
